var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.about",
  title: "EGT About page",
  description: "About EGT — 'A dream, kept.' A scrollytelling film: the dream, the long road, the house of studios, our why, how we build, the founder, the road ahead.",
  supportServer: true,
  fields: { reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false } }
});
var S = require("../helpers/egt.sections.js");

/* The house of studios — five named entrances (LOCKED labels). One elegant
 * line each, a quiet link into the micro-site. Reuses the home wayfinding. */
var ROOMS = [
  { nm: "Universe", key: "universe", line: "The platform that runs a whole business — under your brand.", href: "/platform" },
  { nm: "Intrepid", key: "intrepid", line: "The AI partner that carries the hard parts, quietly.", href: "/intrepid" },
  { nm: "Forge", key: "forge", line: "The creative studio — we build, shoot, brand, and run the campaign.", href: "/forge" },
  { nm: "Studio", key: "studio", line: "Tell your story — the whole film, in your pocket. A studio powered by Intrepid.", href: "/studio" },
  { nm: "Stories", key: "stories", line: "The sound and the story — music today; film and series ahead.", href: "/entertainment" }
];

/* A distinct inline-SVG monogram per room, so each "room" reads as its own
 * object rather than a uniform list item (the Igloo lesson). No asset deps;
 * each is a single stroked figure on a 48-unit grid, inheriting currentColor. */
function monogram(key) {
  var base = { n: "svg", c: "egt-about-room__mono", viewBox: "0 0 48 48", "aria-hidden": "true", focusable: "false" };
  var paths = {
    /* Universe — an orbit ring with a centre point (a whole system) */
    universe: [
      { n: "circle", cx: "24", cy: "24", r: "3.2", fill: "currentColor", stroke: "none" },
      { n: "ellipse", cx: "24", cy: "24", rx: "17", ry: "7.5", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "ellipse", cx: "24", cy: "24", rx: "7.5", ry: "17", fill: "none", stroke: "currentColor", "stroke-width": "1.5" }
    ],
    /* Intrepid — a forward chevron / compass mark (the partner that goes ahead) */
    intrepid: [
      { n: "circle", cx: "24", cy: "24", r: "17", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "path", d: "M18 16l10 8-10 8", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round" }
    ],
    /* Forge — an anvil-like wedge over a baseline (where things are made) */
    forge: [
      { n: "path", d: "M12 19h18a8 8 0 01-8 8h-2l3 7", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round", "stroke-linejoin": "round" },
      { n: "path", d: "M14 34h12", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round" },
      { n: "path", d: "M30 19l4-4", fill: "none", stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round" }
    ],
    /* Studio — a film clapperboard (tell your story) */
    studio: [
      { n: "rect", x: "10", y: "22", width: "28", height: "16", rx: "2", fill: "none", stroke: "currentColor", "stroke-width": "1.6" },
      { n: "path", d: "M10 22L12 15L40 15L38 22Z", fill: "none", stroke: "currentColor", "stroke-width": "1.6", "stroke-linejoin": "round" },
      { n: "path", d: "M18 15l-2 7M26 15l-2 7M34 15l-2 7", fill: "none", stroke: "currentColor", "stroke-width": "1.4", "stroke-linecap": "round" }
    ],
    /* Stories — a play-triangle inside a frame (the sound and the story) */
    stories: [
      { n: "rect", x: "8", y: "10", width: "32", height: "28", rx: "3", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "path", d: "M21 18l9 6-9 6z", fill: "currentColor", stroke: "none" }
    ]
  };
  base.i = paths[key] || paths.universe;
  return base;
}

/* The one faith moment — Serve · Equip · Send, in plain human language. */
var WHY = [
  { word: "Serve", line: "Meet people where they are, with hope and a steady hand." },
  { word: "Equip", line: "Give them the tools to pursue the thing they were made to pursue." },
  { word: "Send", line: "Then get out of the way — so the work, and the person, can go." }
];

/* How we build — values as practiced, not labelled tiles. Each is a clear,
 * declarative pull-quote (also the strongest GEO/AI-legibility beat). */
var ETHICS = [
  { lead: "No dark patterns.", body: "Nothing on this site is built to trap, trick, or wear you down. The exits are always visible — including this one." },
  { lead: "Your data is a grant, never a copy.", body: "What you bring, you keep. We steward it while you're here; we don't quietly hold a second copy when you leave." },
  { lead: "We win when you win.", body: "Honest, transparent pricing. No surprise tiers, no held-hostage features. Our success is downstream of yours." },
  { lead: "Built to be owned.", body: "Dignity for every customer, and software you actually own — not rented access to a thing you can never leave." }
];

exports.render = async function render(options) {
  var reserveUrl = (options && options.reserveUrl) || "/reserve";
  return S.page(reserveUrl, [
    { n: "div", c: "egt-about",
      i: [
        thread(),            /* the signature through-line spine (decorative) */
        hero(),              /* A. opening title card */
        divider(),           /* intentional black breathing room + gold marker */
        chapterDream(),      /* B. the dream */
        divider(),
        chapterRoad(),       /* C. the long road (timeline) */
        chapterHouse(),      /* D. the house it became (solid black — self-separates) */
        chapterWhy(),        /* E. our why — the one faith moment */
        divider(),
        chapterBuild(),      /* F. how we build — ethics */
        divider(),
        chapterFounder(),    /* G. the founder, in one frame */
        chapterAhead(reserveUrl), /* H. the road ahead + invitation */
        footnote()           /* I. honesty footnote */
      ] },
    /* JSON-LD: AboutPage + BreadcrumbList (SEO wave 2B-7) */
    S.pageSchema({ path: "/about", name: "About EGT", type: "AboutPage", about: "/#organization",
      crumbs: [ { name: "About" } ] })
  ]);
};

/* ---- the through-line spine (decorative; JS draws + travels the word) ---- */
function thread() {
  return {
    n: "div", c: "egt-thread", id: "egt-thread", "aria-hidden": "true",
    i: [
      { n: "div", c: "egt-thread__line", i: { n: "span", c: "egt-thread__fill", id: "egt-thread-fill" } },
      { n: "div", c: "egt-thread__word", id: "egt-thread-word", i: "dream" }
    ]
  };
}

/* ---- intentional chapter separation ------------------------------------
 * ~10rem of true-black breathing room between stacked media chapters, made to
 * feel DELIBERATE rather than an empty void: a thin gold hairline rule with a
 * small centered gold diamond glyph sitting on it (the line passes behind the
 * glyph, masked by the black). Decorative only (aria-hidden). Reusable via
 * .egt-about__divider — used between A/B, B/C, E/F, F/G in render(). */
function divider() {
  return {
    n: "div", c: "egt-about__divider", "aria-hidden": "true",
    i: { n: "span", c: "egt-about__divider-mark",
      i: { n: "svg", c: "egt-about__divider-glyph", viewBox: "0 0 16 16", focusable: "false",
        i: [
          { n: "path", d: "M8 1l7 7-7 7-7-7z", fill: "none", stroke: "currentColor", "stroke-width": "1" },
          { n: "circle", cx: "8", cy: "8", r: "1.5", fill: "currentColor", stroke: "none" }
        ] } }
  };
}

/* ---- A. HERO — the opening title card ---------------------------------- */
function hero() {
  return {
    n: "section", c: "egt-canvas egt-about-hero",
    i: [
      S.media({ img: "/imgs/egt-about-canvas.jpg", alt: "", parallax: 0.1, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-about-hero__inner",
        i: { n: "div", c: "egt-glass egt-about-hero__card egt-reveal", "data-egt-tilt": "true",
          i: [
            { n: "p", c: "egt__eyebrow egt-reveal", style: "--egt-i:0", i: "About EGT" },
            { n: "h1", c: "egt-about-hero__title egt-reveal", style: "--egt-i:1",
              i: [ "A dream, ", { n: "span", c: "egt__gold", i: "kept." } ] },
            { n: "p", c: "egt-about-hero__lead egt-reveal", style: "--egt-i:3",
              i: "Twenty years on one idea — a house of studios where you shouldn't have to become an engineer to exist online." },
            /* answer-first (SEO wave 2C-5): what EGT is, plainly, in one place */
            { n: "p", c: "egt-about-hero__answer egt-reveal", style: "--egt-i:4",
              i: "EGT is a house of studios — software, AI, creative, apps and music — founded by Justin K Kazmierczak in Columbus, Ohio. It builds the Universe Platform for businesses, runs the EGT Forge agency, and tells the stories of The Universe." }
          ] } },
      { n: "a", href: "#egt-about-dream", c: "egt-about-hero__cue", "aria-label": "Begin the story", i: "⌄" }
    ]
  };
}

/* ---- B. THE DREAM — sticky serif line, media beside it ----------------- */
function chapterDream() {
  return {
    /* NOT .egt-canvas — that sets overflow:hidden, which would clamp the
     * sticky chapter label to this short section. The media is clipped by its
     * own wrapper instead, leaving the sticky's ancestor chain clip-free. */
    n: "section", c: "egt-about-scene egt-about-scene--dream", id: "egt-about-dream",
    i: [
      { n: "div", c: "egt-about-scene__bg", "aria-hidden": "true",
        i: S.media({ img: "/imgs/about/about-vision.jpg", alt: "", parallax: 0.14 }) },
      { n: "div", c: "egt__shell egt-about-scene__grid",
        i: [
          { n: "div", c: "egt-about-scene__sticky",
            i: { n: "p", c: "egt-about-chapno egt-reveal", i: "Chapter one" } },
          { n: "div", c: "egt-glass egt-glass--wide egt-about-scene__card egt-reveal egt-reveal--up",
            i: [
              { n: "h2", c: "egt-about-scene__word",
                i: [ "It started as a ", { n: "span", c: "egt__gold", i: "vision." } ] },
              { n: "p", c: "egt-about-scene__copy",
                i: "Around the age of ten, a picture arrived and never left: a brighter future where technology and entertainment lift people up instead of wearing them down — and where the everyday person gets to chase the thing they were made to chase." },
              { n: "p", c: "egt-about-scene__copy",
                i: "It wasn't a business plan. It was a conviction. Everything since has been the slow, stubborn work of keeping it." }
            ] }
        ] }
    ]
  };
}

/* ---- C. THE LONG ROAD — scroll-driven timeline, chapters of one story --- */
var BEATS = [
  { yr: "The early years", h: "Live, in the room.", media: "/imgs/egt-about-road.jpg",
    c: "Twenty-plus years producing, directing, and editing — live events and commercial spots, learning that the work only matters when a real audience feels it." },
  { yr: "The craft years", h: "Brands, frame by frame.", media: "/imgs/egt-about-craft.jpg",
    c: "Years of video, design, and campaign craft for businesses of every size. The lesson that compounded: most people don't lack ideas — they lack the tools to ship them." },
  { yr: "The turn", h: "A platform begins.", media: "/imgs/about/about-machinery.jpg",
    c: "The same problem, again and again: a beautiful idea, blocked by the machinery underneath it. So the work turned to building the machinery itself — quietly, for years." },
  { yr: "The partner", h: "An AI joins the house.", media: "/imgs/egt-about-why.jpg",
    c: "Then a partner that could carry the hard, repetitive parts — so the human side stays human. One studio, still learning as it grows." }
];

function chapterRoad() {
  return {
    /* NOT .egt-canvas — overflow:hidden there would clamp the sticky chapter
     * head to this section. The media layers are clipped by .egt-about-road__media
     * itself; the sticky's ancestor chain stays clip/transform-free. */
    n: "section", c: "egt-about-road", id: "egt-about-road",
    i: [
      /* a stacked set of warm per-beat media layers; JS cross-fades opacity as
       * each beat enters (one IntersectionObserver). Layer 0 is the poster /
       * reduced-motion default (rendered fully via CSS). No parallax on the
       * layers: a Y-translate on an inset:0 layer can pull a dark edge in. */
      { n: "div", c: "egt-about-road__media", "aria-hidden": "true",
        i: BEATS.map(function (b, idx) {
          return {
            n: "div", c: "egt-about-road__layer" + (idx === 0 ? " is-active" : ""),
            "data-beat-layer": String(idx),
            i: { n: "img", c: "egt-canvas__img", src: b.media, alt: "",
              loading: idx === 0 ? undefined : "lazy",
              fetchpriority: idx === 0 ? "high" : undefined, decoding: "async" }
          };
        }) },
      { n: "div", c: "egt__shell egt-about-road__inner",
        i: [
          { n: "div", c: "egt-about-road__head",
            i: { n: "div", c: "egt-glass egt-about-road__headcard egt-reveal egt-reveal--up",
              i: [
                { n: "p", c: "egt-about-chapno", i: "Chapter two" },
                { n: "h2", c: "egt-about-road__title", i: "The long road." },
                { n: "p", c: "egt-about-road__lead",
                  i: "Not a résumé — chapters of one story. Each step taught the next how to exist." }
              ] } },
          { n: "ol", c: "egt-about-timeline",
            i: BEATS.map(function (b, idx) {
              return {
                n: "li", c: "egt-about-beat egt-reveal egt-reveal--up", style: "--egt-i:" + idx,
                "data-beat": String(idx),
                i: { n: "div", c: "egt-glass egt-about-beat__card egt-lift",
                  i: [
                    { n: "span", c: "egt-about-beat__dot", "aria-hidden": "true" },
                    { n: "p", c: "egt-about-beat__yr", i: b.yr },
                    { n: "h3", c: "egt-about-beat__h", i: b.h },
                    { n: "p", c: "egt-about-beat__c", i: b.c }
                  ] }
              };
            }) }
        ] }
    ]
  };
}

/* ---- D. THE HOUSE IT BECAME — five named entrances --------------------- */
function chapterHouse() {
  return {
    n: "section", c: "egt-section egt-about-house",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-about-house__head egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt-about-chapno", i: "Chapter three" },
            { n: "h2", c: "egt-about-house__title",
              i: [ "One studio. ", { n: "br" }, "Many ", { n: "span", c: "egt__gold", i: "rooms." } ] },
            { n: "p", c: "egt-about-house__lead",
              i: "EGT is a house of studios. The rooms that ship today help fund the entertainment dream ahead — that's the whole strategy, said plainly." }
          ] },
        { n: "ul", c: "egt-about-rooms",
          i: ROOMS.map(function (r, idx) {
            return {
              n: "li", c: "egt-about-room egt-reveal egt-reveal--up", style: "--egt-i:" + idx,
              i: { n: "a", c: "egt-about-room__link", href: r.href,
                "data-egt-tilt": "true", "data-room": r.key,
                "aria-label": r.nm + " — " + r.line,
                i: [
                  { n: "span", c: "egt-about-room__figure", "aria-hidden": "true",
                    /* Intrepid carries its REAL brand mark (the husky) — a full-color
                     * img in the same figure slot; the rest stay line-icon monograms. */
                    i: r.key === "intrepid"
                      ? { n: "img", c: "egt-about-room__mark", src: "/imgs/intrepid-mark.svg", alt: "", loading: "lazy", decoding: "async" }
                      : monogram(r.key) },
                  { n: "span", c: "egt-about-room__body",
                    i: [
                      { n: "span", c: "egt-about-room__nm egt-underline", i: r.nm },
                      { n: "span", c: "egt-about-room__line", i: r.line }
                    ] },
                  { n: "span", c: "egt-about-room__go", "aria-hidden": "true",
                    i: [ "Enter", { n: "span", c: "egt-about-room__go-tick", i: " ›" } ] }
                ] }
            };
          }) }
      ] }
  };
}

/* ---- E. OUR WHY — the one faith moment (gold used once, calm imagery) --- */
function chapterWhy() {
  return {
    n: "section", c: "egt-canvas egt-canvas--center egt-canvas--tall egt-about-why",
    i: [
      S.media({ img: "/imgs/about/about-hands.jpg", alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-about-why__inner",
        i: { n: "div", c: "egt-glass egt-glass--wide egt-about-why__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: "Our why" },
            { n: "h2", c: "egt-about-why__title",
              i: [ "Serve. Equip. ", { n: "span", c: "egt__gold", i: "Send." } ] },
            { n: "p", c: "egt-about-why__lead",
              i: "Faith is woven through how we work — quietly, never as a wedge. If you want the short version of why this house exists, it's three words:" },
            { n: "ul", c: "egt-about-why__list",
              i: WHY.map(function (w, idx) {
                return {
                  n: "li", c: "egt-about-why__row egt-reveal", style: "--egt-i:" + idx,
                  i: [
                    { n: "span", c: "egt-about-why__word", i: w.word },
                    { n: "span", c: "egt-about-why__line", i: w.line }
                  ] };
              }) }
          ] } }
    ]
  };
}

/* ---- F. HOW WE BUILD — ethics as practiced (declarative pull-quotes) ---- */
function chapterBuild() {
  return {
    n: "section", c: "egt-canvas egt-about-build",
    i: [
      S.media({ img: "/imgs/about/about-craft.jpg", alt: "", parallax: 0.12 }),
      { n: "div", c: "egt__shell egt-about-build__inner",
        i: [
          { n: "div", c: "egt-glass egt-about-build__head egt-reveal egt-reveal--up",
            i: [
              { n: "p", c: "egt-about-chapno", i: "Chapter four" },
              { n: "h2", c: "egt-about-build__title", i: "How we build." },
              { n: "p", c: "egt-about-build__lead",
                i: "Values you can check us on — not labels on a wall. The same standard runs through every room of the house." }
            ] },
          { n: "div", c: "egt-about-ethics",
            i: ETHICS.map(function (e, idx) {
              return {
                n: "figure", c: "egt-glass egt-about-ethic egt-lift egt-reveal egt-reveal--up", style: "--egt-i:" + idx,
                i: [
                  { n: "blockquote", c: "egt-about-ethic__lead", i: e.lead },
                  { n: "figcaption", c: "egt-about-ethic__body", i: e.body }
                ] };
            }) }
        ] }
    ]
  };
}

/* ---- G. THE FOUNDER, IN ONE FRAME -------------------------------------- */
function chapterFounder() {
  return {
    n: "section", c: "egt-canvas egt-about-founder",
    i: [
      S.media({ img: "/imgs/about/about-studio.jpg", alt: "", parallax: 0.1 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-about-founder__inner",
        i: { n: "figure", c: "egt-glass egt-glass--wide egt-about-founder__card egt-reveal egt-reveal--up",
          i: [
            { n: "p", c: "egt__eyebrow", i: "The founder, in one frame" },
            { n: "blockquote", c: "egt-about-founder__quote",
              i: "Your website can now power your entire business — and we don't make a single decision about how you run it." },
            { n: "p", c: "egt-about-founder__note",
              i: "I'm the sole principal, working alongside a small circle of trusted contractors. That means every promise on this page has one name behind it — and I intend to keep it." },
            { n: "figcaption", c: "egt-about-founder__attr",
              i: [ { n: "span", c: "egt-about-founder__nm", i: "Justin Kazmierczak" },
                { n: "span", c: "egt-about-founder__role", i: "Founder · EGT · Columbus, Ohio" } ] }
          ] } }
    ]
  };
}

/* ---- H. THE ROAD AHEAD + INVITATION ------------------------------------ */
function chapterAhead(reserveUrl) {
  return {
    n: "section", c: "egt-section egt-about-ahead",
    i: { n: "div", c: "egt__shell egt-about-ahead__inner egt-reveal egt-reveal--up",
      i: [
        { n: "p", c: "egt-about-chapno", i: "The road ahead" },
        { n: "h2", c: "egt-about-ahead__title",
          i: [ "The dream the rest of the house is here to ", { n: "span", c: "egt__gold", i: "fund." } ] },
        { n: "p", c: "egt-about-ahead__lead",
          i: "Entertainment that moves people — music, film, and new media, built on the same universe. If you'd like to be part of it, there's a place for you." },
        { n: "div", c: "egt-about-ahead__actions",
          i: [
            S.cta(reserveUrl, "Join Beta", true),
            { n: "a", href: "/forge", c: "egt-cta egt-cta--ghost",
              i: [ "Or explore the studios", { n: "span", c: "egt-cta__tick", i: " ›" } ] }
          ] }
      ] }
  };
}

/* ---- I. HONESTY FOOTNOTE ----------------------------------------------- */
function footnote() {
  return {
    n: "section", c: "egt-section egt-about-note",
    i: { n: "div", c: "egt__shell egt-reveal",
      i: { n: "p", c: "egt-about-note__txt",
        i: [
          "Some of what you see here is illustrative. Nothing on this page promises a date. By using this site you accept the ",
          { n: "a", href: "/legal/codex", c: "egt-underline", i: "EGT Codex" },
          "."
        ] } }
  };
}
